import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button, Drawer, Spin, Tag, Empty, message, Modal, Input, Select, Collapse,
  Radio, Upload, Dropdown, Menu,
} from 'antd';
import { PlusCircleOutlined, UploadOutlined } from '@ant-design/icons';
import {
  getReferralTemplates,
  createReferralTemplate,
  updateReferralTemplate,
  deleteReferralTemplate,
  archiveReferralTemplate,
  unarchiveReferralTemplate,
  duplicateReferralTemplate,
  duplicateStarterTemplate,
  downloadReferralTemplate,
} from '../../../actions/referralTemplates';
import { trackReferralTemplateEvent } from '../../../global/analytics';
import './manage-referral-templates.scss';

const { Option } = Select;
const { Panel } = Collapse;
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DEFAULT_STARTER_KEY = 'basic_reply_template';
const UNDELETABLE_TOOLTIP = 'In use by an auto-reply status or past replies — unlink it first.';
const UNARCHIVABLE_TOOLTIP = 'In use by an auto-reply status — unlink it first, then archive.';
// Above this many owner groups, collapse department groups by default so big
// orgs (the largest has 680 templates across 88 departments) stay navigable.
const EXPAND_ALL_BELOW = 6;

// Lazy-load the heavy editor (ProseMirror + the docx core) only when a user
// actually opens it, mirroring how the document viewers are code-split.
const loadEditor = () => import(
  /* webpackChunkName: "referral-template-editor" */
  '../../referral_templates/ReferralTemplateEditor'
);

const ownerLabel = (t) => (t.owner_type === 'Department' ? (t.owner_name || 'Department') : 'Whole organization');

class ManageReferralTemplates extends Component {
  constructor(props) {
    super(props);
    this.state = {
      drawerOpen: false,
      editingSlug: null,
      editingName: '',
      // lock_version of the template being edited; sent back on save so
      // a concurrent edit surfaces as a 409 instead of a silent overwrite.
      editingLockVersion: null,
      editorDirty: false,
      docxBuffer: null,
      EditorComponent: null,
      busy: false,
      search: '',
      ownerFilter: 'all',
      nameFilter: 'all',
      // Archived templates are hidden by default; the toolbar select can show them.
      statusFilter: 'active',
      openGroups: null,
      createModalOpen: false,
      createSource: 'starter',
      createStarterKey: null,
      createName: '',
      // Once the user types a custom name, stop syncing it to the starter label.
      createNameTouched: false,
      createUploadFile: null,
      createOwnerType: 'Organization',
      createOwnerId: null,
      renameOpen: false,
      renameSlug: null,
      renameValue: '',
      ownerOpen: false,
      ownerSlug: null,
      ownerTypeValue: 'Organization',
      ownerIdValue: null,
    };
  }

  componentDidMount() {
    this.refresh();
  }

  refresh = async () => {
    await this.props.getReferralTemplates();
  };

  ensureEditorLoaded = async () => {
    if (this.state.EditorComponent) return this.state.EditorComponent;
    const mod = await loadEditor();
    this.setState({ EditorComponent: mod.default });
    return mod.default;
  };

  openEditor = async (template) => {
    this.setState({ busy: true });
    try {
      await this.ensureEditorLoaded();
      const res = await this.props.downloadReferralTemplate(template.slug);
      if (res.error) {
        if (res.response?.status === 404) {
          message.error('This template has no document file in storage, so it can’t be opened. Duplicate another template or delete this one.', 6);
        } else {
          message.error('Could not open this template. Please try again.');
        }
        return;
      }
      trackReferralTemplateEvent({ action: 'editor_opened' });
      this.setState({
        drawerOpen: true,
        editingSlug: template.slug,
        editingName: template.template_name,
        editingLockVersion: template.lock_version,
        editorDirty: false,
        docxBuffer: res.data,
      });
    } finally {
      this.setState({ busy: false });
    }
  };

  handleDirtyChange = (isDirty) => this.setState({ editorDirty: isDirty });

  // Editor-requested close (Cancel button, ESC). Guard unsaved edits.
  closeEditor = () => {
    if (this.state.editorDirty) {
      Modal.confirm({
        title: 'Discard unsaved changes?',
        content: 'Your edits to this template will be lost.',
        okText: 'Discard',
        okType: 'danger',
        onOk: this.doCloseEditor,
      });
      return;
    }
    this.doCloseEditor();
  };

  doCloseEditor = () => {
    this.setState({
      drawerOpen: false,
      editingSlug: null,
      editingName: '',
      editingLockVersion: null,
      editorDirty: false,
      docxBuffer: null,
    });
  };

  // Editor contract: return true on success; the drawer closes only when the
  // save succeeded and the editor didn't ask to stay open ("Save" vs "Save & close").
  handleSave = async (arrayBuffer, { keepOpen } = {}) => {
    const { editingSlug, editingLockVersion } = this.state;
    const formData = new FormData();
    formData.append('file', new Blob([arrayBuffer], { type: DOCX_MIME }), 'template.docx');
    if (editingLockVersion !== null && editingLockVersion !== undefined) {
      formData.append('expected_lock_version', editingLockVersion);
    }
    const res = await this.props.updateReferralTemplate(editingSlug, formData);
    if (res.error) {
      if (res.response?.status === 409) {
        message.error(res.response?.data?.error || 'This template changed while you were editing it.', 6);
      } else {
        message.error('Could not save the template. Please try again.');
      }
      return false;
    }
    message.success('Template saved');
    if (keepOpen) {
      // Track the fresh lock_version so the next save doesn't 409 against our own write.
      this.setState({ editingLockVersion: res.data.referral_template.lock_version });
    } else {
      this.doCloseEditor();
    }
    this.refresh();
    return true;
  };

  handleDuplicate = async (template) => {
    this.setState({ busy: true });
    try {
      const res = await this.props.duplicateReferralTemplate(template.slug);
      if (res.error) {
        message.error('Could not duplicate this template.');
        return;
      }
      message.success(`Duplicated “${template.template_name}”`);
      trackReferralTemplateEvent({ action: 'duplicated' });
      await this.refresh();
    } finally {
      this.setState({ busy: false });
    }
  };

  confirmDelete = (template) => {
    Modal.confirm({
      title: 'Delete this template? This can\'t be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => this.handleDelete(template),
    });
  };

  handleDelete = async (template) => {
    const res = await this.props.deleteReferralTemplate(template.slug);
    if (res.error) {
      message.error(res.response?.data?.error || 'Could not delete this template.');
      return;
    }
    message.success('Template deleted');
    trackReferralTemplateEvent({ action: 'deleted' });
    this.refresh();
  };

  handleDownload = async (template) => {
    const res = await this.props.downloadReferralTemplate(template.slug);
    if (res.error) {
      if (res.response?.status === 404) {
        message.error('This template has no document file in storage to download.', 6);
      } else {
        message.error('Could not download this template. Please try again.');
      }
      return;
    }
    const url = window.URL.createObjectURL(new Blob([res.data], { type: DOCX_MIME }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${template.template_name}.docx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    trackReferralTemplateEvent({ action: 'downloaded' });
  };

  handleRowMenuClick = (key, template) => {
    if (key === 'rename') this.openRenameModal(template);
    else if (key === 'owner') this.openOwnerModal(template);
    else if (key === 'download') this.handleDownload(template);
    // Same enabled-but-muted treatment as Delete: a disabled menu item swallows
    // hover, so explain via a message instead of a tooltip that never fires.
    else if (key === 'archive') {
      if (template.archivable) this.handleArchive(template);
      else {
        trackReferralTemplateEvent({ action: 'archive_blocked' });
        message.info(UNARCHIVABLE_TOOLTIP, 5);
      }
    } else if (key === 'unarchive') this.handleUnarchive(template);
    // Undeletable templates keep an enabled-but-muted Delete item so the reason
    // is discoverable — a disabled menu item swallows hover, so its tooltip
    // never fires. Explain via a message instead of opening the confirm.
    else if (key === 'delete') {
      if (template.deletable) this.confirmDelete(template);
      else message.info(UNDELETABLE_TOOLTIP, 5);
    }
  };

  handleArchive = async (template) => {
    const res = await this.props.archiveReferralTemplate(template.slug);
    if (res.error) {
      message.error(res.response?.data?.error || 'Could not archive this template.');
      return;
    }
    message.success(`Archived “${template.template_name}”. It no longer appears when sending replies.`);
    trackReferralTemplateEvent({ action: 'archived' });
    this.refresh();
  };

  handleUnarchive = async (template) => {
    const res = await this.props.unarchiveReferralTemplate(template.slug);
    if (res.error) {
      message.error(res.response?.data?.error || 'Could not unarchive this template.');
      return;
    }
    message.success(`Unarchived “${template.template_name}”.`);
    trackReferralTemplateEvent({ action: 'unarchived' });
    this.refresh();
  };

  // ---- Create modal ----

  openCreateModal = (starter) => {
    this.setState({
      createModalOpen: true,
      createSource: 'starter',
      createStarterKey: starter ? starter.key : null,
      createName: starter ? starter.template_name : '',
      createNameTouched: false,
      createUploadFile: null,
      createOwnerType: 'Organization',
      createOwnerId: null,
    });
  };

  openCreateModalFromCta = () => {
    const { starters } = this.props;
    const starter = starters.find((s) => s.key === DEFAULT_STARTER_KEY) || starters[0];
    this.openCreateModal(starter);
  };

  closeCreateModal = () => this.setState({ createModalOpen: false, createStarterKey: null, createUploadFile: null });

  handleSourceChange = (value) => {
    this.setState((prev) => ({
      createSource: value,
      // Per-department copies only exist for starters; fall back to org-wide.
      createOwnerType: value === 'upload' && prev.createOwnerType === 'AllDepartments' ? 'Organization' : prev.createOwnerType,
    }));
  };

  handleStarterChange = (key) => {
    const starter = this.props.starters.find((s) => s.key === key);
    this.setState((prev) => ({
      createStarterKey: key,
      createName: !prev.createNameTouched && starter ? starter.template_name : prev.createName,
    }));
  };

  handleUploadPick = (file) => {
    if (!file.name.toLowerCase().endsWith('.docx')) {
      message.error('Referral reply templates must be a .docx file.');
      // Keep the rejected file out of the controlled list entirely.
      return Upload.LIST_IGNORE;
    }
    this.setState({ createUploadFile: file });
    return false; // never auto-post; we submit via FormData on confirm
  };

  handleConfirmCreate = async () => {
    const {
      createSource, createStarterKey, createName, createOwnerType, createOwnerId, createUploadFile,
    } = this.state;
    const name = createName.trim();
    if (!name) {
      message.error('Please enter a template name.');
      return;
    }
    if (createSource === 'upload' && !createUploadFile) {
      message.error('Please choose a .docx file to upload.');
      return;
    }
    if (createOwnerType === 'Department' && !createOwnerId) {
      message.error('Please choose a department.');
      return;
    }
    this.setState({ busy: true });
    try {
      if (createOwnerType === 'AllDepartments') {
        const res = await this.props.duplicateStarterTemplate(createStarterKey, name, 'Organization', null, true);
        if (res.error) {
          message.error(res.response?.data?.error || 'Could not create the templates.');
          return;
        }
        message.success(`Created ${(res.data.referral_templates || []).length} templates`);
        trackReferralTemplateEvent({ action: 'created', source: 'starter', allDepartments: true });
        this.closeCreateModal();
        await this.refresh();
        return;
      }

      let res;
      if (createSource === 'starter') {
        res = await this.props.duplicateStarterTemplate(createStarterKey, name, createOwnerType, createOwnerId);
      } else {
        const formData = new FormData();
        formData.append('template_name', name);
        formData.append('owner_type', createOwnerType);
        if (createOwnerType === 'Department') formData.append('owner_id', createOwnerId);
        formData.append('file', createUploadFile);
        res = await this.props.createReferralTemplate(formData);
      }
      if (res.error) {
        message.error(res.response?.data?.error || 'Could not create the template.');
        return;
      }
      trackReferralTemplateEvent({ action: 'created', source: createSource, allDepartments: false });
      this.closeCreateModal();
      await this.refresh();
      await this.openEditor(res.data.referral_template);
    } finally {
      this.setState({ busy: false });
    }
  };

  // ---- Rename modal ----

  openRenameModal = (template) => this.setState({
    renameOpen: true, renameSlug: template.slug, renameValue: template.template_name,
  });

  closeRenameModal = () => this.setState({ renameOpen: false, renameSlug: null, renameValue: '' });

  handleConfirmRename = async () => {
    const { renameSlug, renameValue } = this.state;
    const name = renameValue.trim();
    if (!name) {
      message.error('Please enter a template name.');
      return;
    }
    this.setState({ busy: true });
    try {
      const res = await this.props.updateReferralTemplate(renameSlug, { template_name: name });
      if (res.error) {
        message.error(res.response?.data?.error || 'Could not rename this template.');
        return;
      }
      message.success('Template renamed');
      trackReferralTemplateEvent({ action: 'renamed' });
      this.closeRenameModal();
      await this.refresh();
    } finally {
      this.setState({ busy: false });
    }
  };

  // ---- Change owner modal ----

  openOwnerModal = (template) => this.setState({
    ownerOpen: true,
    ownerSlug: template.slug,
    ownerTypeValue: template.owner_type === 'Department' ? 'Department' : 'Organization',
    ownerIdValue: template.owner_type === 'Department' ? template.owner_id : null,
  });

  closeOwnerModal = () => this.setState({ ownerOpen: false, ownerSlug: null });

  handleConfirmOwner = async () => {
    const { ownerSlug, ownerTypeValue, ownerIdValue } = this.state;
    if (ownerTypeValue === 'Department' && !ownerIdValue) {
      message.error('Please choose a department.');
      return;
    }
    this.setState({ busy: true });
    try {
      const res = await this.props.updateReferralTemplate(ownerSlug, {
        owner_type: ownerTypeValue,
        owner_id: ownerTypeValue === 'Department' ? ownerIdValue : null,
      });
      if (res.error) {
        message.error(res.response?.data?.error || 'Could not change the owner.');
        return;
      }
      message.success('Owner updated');
      trackReferralTemplateEvent({ action: 'owner_changed' });
      this.closeOwnerModal();
      await this.refresh();
    } finally {
      this.setState({ busy: false });
    }
  };

  // Templates passing the status filter alone. Shared by the visible list and
  // the name-filter counts so the two can never disagree: the status filter is
  // the only one that is ON by default, so counting names over the unfiltered
  // list made every admin's first page load advertise counts that included rows
  // the list was hiding.
  statusFilteredTemplates = () => {
    const { statusFilter } = this.state;
    return this.props.templates.filter((t) => {
      if (statusFilter === 'active' && t.active === false) return false;
      if (statusFilter === 'archived' && t.active !== false) return false;
      return true;
    });
  };

  // All templates passing the current search + owner + status filter.
  filteredTemplates = () => {
    const { search, ownerFilter, nameFilter } = this.state;
    const term = search.trim().toLowerCase();
    return this.statusFilteredTemplates().filter((t) => {
      if (term && !t.template_name.toLowerCase().includes(term)) return false;
      if (nameFilter !== 'all' && t.template_name !== nameFilter) return false;
      if (ownerFilter === 'all') return true;
      if (ownerFilter === 'org') return t.owner_type !== 'Department';
      return t.owner_type === 'Department' && `dept:${t.owner_id}` === ownerFilter;
    });
  };

  // Distinct template names with counts — lets a user pick "Insurance not
  // accepted" and see every department's copy of it. (We have no real "type",
  // so distinct name is the closest thing.) Counts come from the status-filtered
  // set so picking a name always yields the rows the count promised.
  distinctNames = () => {
    const counts = new Map();
    this.statusFilteredTemplates().forEach((t) => {
      counts.set(t.template_name, (counts.get(t.template_name) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  // Group filtered templates by owner (org-wide first, then departments A→Z).
  groupedTemplates = (list) => {
    const groups = new Map();
    list.forEach((t) => {
      const isOrg = t.owner_type !== 'Department';
      const key = isOrg ? 'org' : `dept:${t.owner_id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key, label: ownerLabel(t), isOrg, templates: [],
        });
      }
      groups.get(key).templates.push(t);
    });
    return Array.from(groups.values()).sort((a, b) => {
      if (a.isOrg !== b.isOrg) return a.isOrg ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
  };

  renderDepartmentOptions = () => this.props.departments.map((dept) => (
    <Option key={dept.id} value={dept.id}>{dept.name}</Option>
  ));

  renderRowMenu = (template) => (
    <Menu onClick={({ key }) => this.handleRowMenuClick(key, template)}>
      <Menu.Item key="rename">Rename</Menu.Item>
      <Menu.Item key="owner">Change owner</Menu.Item>
      <Menu.Item key="download">Download</Menu.Item>
      {template.active === false && (
        <Menu.Item key="unarchive">Unarchive</Menu.Item>
      )}
      {template.active !== false && (template.archivable !== false ? (
        <Menu.Item key="archive">Archive</Menu.Item>
      ) : (
        <Menu.Item key="archive" className="mrt-menu-item--muted">Archive</Menu.Item>
      ))}
      {template.deletable ? (
        <Menu.Item key="delete" danger>Delete</Menu.Item>
      ) : (
        <Menu.Item key="delete" className="mrt-menu-item--muted">Delete</Menu.Item>
      )}
    </Menu>
  );

  renderTemplateRow = (template) => (
    <div className={`mrt-card${template.active === false ? ' mrt-card--archived' : ''}`} key={template.slug}>
      <div className="mrt-card__main">
        <div className="mrt-card__name">
          {template.template_name}
          {template.active === false && <Tag className="mrt-card__archived-tag">Archived</Tag>}
        </div>
        {template.linked_statuses.length > 0 && (
          <div className="mrt-card__meta">
            {template.linked_statuses.map((status) => (
              <Tag key={status} className="mrt-card__status-tag">{`Auto-reply: ${status}`}</Tag>
            ))}
          </div>
        )}
      </div>
      <div className="mrt-card__actions">
        <Button type="primary" ghost size="small" onClick={() => this.openEditor(template)}>
          Edit
        </Button>
        <Button size="small" onClick={() => this.handleDuplicate(template)}>Duplicate</Button>
        <Dropdown overlay={this.renderRowMenu(template)} trigger={['click']}>
          <Button size="small" className="mrt-card__more" aria-label="More actions">⋯</Button>
        </Dropdown>
      </div>
    </div>
  );

  renderList() {
    const { isLoading, templates } = this.props;
    const {
      search, ownerFilter, nameFilter, statusFilter, openGroups,
    } = this.state;
    const filtered = this.filteredTemplates();

    if (!isLoading && filtered.length === 0) {
      // The status filter defaults to "active", so an org whose templates are
      // all archived must still see the filter hint, not the first-run CTA.
      const isFiltering = templates.length > 0
        || search.trim() || ownerFilter !== 'all' || nameFilter !== 'all';
      return (
        <Empty description={isFiltering ? 'No templates match your filters.' : 'No templates yet. Click “New template” to create your first one.'} />
      );
    }

    const groups = this.groupedTemplates(filtered);
    // While filtering/searching, open every matching group. Otherwise honor the
    // user's manual toggles (openGroups), defaulting to all-open for small orgs
    // and just the org-wide group for big ones.
    const autoExpand = !!search.trim() || ownerFilter !== 'all' || nameFilter !== 'all' || statusFilter !== 'active';
    const defaultKeys = groups.length <= EXPAND_ALL_BELOW ? groups.map((g) => g.key) : ['org'];
    const manualKeys = openGroups !== null ? openGroups : defaultKeys;
    const activeKey = autoExpand ? groups.map((g) => g.key) : manualKeys;

    return (
      <Collapse
        ghost
        className="mrt-collapse"
        activeKey={activeKey}
        onChange={(keys) => this.setState({ openGroups: keys })}
      >
        {groups.map((group) => (
          <Panel
            key={group.key}
            header={(
              <span className="mrt-group__header">
                <span className="mrt-group__label">{group.label}</span>
                <span className="mrt-group__count">{group.templates.length}</span>
              </span>
            )}
          >
            <div className="mrt-group__rows">{group.templates.map(this.renderTemplateRow)}</div>
          </Panel>
        ))}
      </Collapse>
    );
  }

  renderCreateModal() {
    const { starters, departments } = this.props;
    const {
      createModalOpen, createSource, createStarterKey, createName,
      createOwnerType, createOwnerId, createUploadFile, busy,
    } = this.state;

    return (
      <Modal
        title="New template"
        visible={createModalOpen}
        onOk={this.handleConfirmCreate}
        onCancel={this.closeCreateModal}
        okText={createOwnerType === 'AllDepartments' ? 'Create templates' : 'Create & edit'}
        confirmLoading={busy}
      >
        <div className="mrt-create-field">
          <span className="mrt-create-field__label">Start from</span>
          <Radio.Group
            className="mrt-create-source"
            value={createSource}
            onChange={(e) => this.handleSourceChange(e.target.value)}
          >
            <Radio value="starter">Start from a Medsender template</Radio>
            <Radio value="upload">Upload a .docx file</Radio>
          </Radio.Group>
        </div>
        {createSource === 'starter' && (
          <div className="mrt-create-field">
            <span className="mrt-create-field__label">Medsender template</span>
            <Select
              style={{ width: '100%' }}
              placeholder="Choose a template"
              value={createStarterKey || undefined}
              onChange={this.handleStarterChange}
            >
              {starters.map((starter) => (
                <Option key={starter.key} value={starter.key}>{starter.template_name}</Option>
              ))}
            </Select>
          </div>
        )}
        {createSource === 'upload' && (
          <div className="mrt-create-field">
            <span className="mrt-create-field__label">.docx file</span>
            <Upload
              accept=".docx"
              maxCount={1}
              beforeUpload={this.handleUploadPick}
              onRemove={() => this.setState({ createUploadFile: null })}
              fileList={createUploadFile
                ? [{ uid: createUploadFile.uid || '-1', name: createUploadFile.name, status: 'done' }]
                : []}
            >
              <Button icon={<UploadOutlined />}>Choose a .docx file</Button>
            </Upload>
          </div>
        )}
        <div className="mrt-create-field">
          <span className="mrt-create-field__label">Template name</span>
          <Input
            value={createName}
            onChange={(e) => this.setState({ createName: e.target.value, createNameTouched: true })}
            placeholder="e.g. Patient declined"
          />
        </div>
        <div className="mrt-create-field">
          <span className="mrt-create-field__label">Who can use this template?</span>
          <Select
            value={createOwnerType}
            style={{ width: '100%' }}
            onChange={(value) => this.setState({ createOwnerType: value, createOwnerId: null })}
          >
            <Option value="Organization">Whole organization</Option>
            <Option value="Department">A specific department</Option>
            {createSource === 'starter' && departments.length > 0 && (
              <Option value="AllDepartments">One copy per department</Option>
            )}
          </Select>
          {createOwnerType === 'AllDepartments' && (
            <p className="mrt-create-field__hint">
              {`Creates one editable copy for each of your ${departments.length} departments.`}
            </p>
          )}
        </div>
        {createOwnerType === 'Department' && (
          <div className="mrt-create-field">
            <span className="mrt-create-field__label">Department</span>
            <Select
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              placeholder="Choose a department"
              value={createOwnerId || undefined}
              onChange={(value) => this.setState({ createOwnerId: value })}
            >
              {this.renderDepartmentOptions()}
            </Select>
          </div>
        )}
      </Modal>
    );
  }

  render() {
    const { isLoading, isSaving, departments } = this.props;
    const {
      drawerOpen, editingName, docxBuffer, EditorComponent, busy,
      search, ownerFilter, nameFilter, statusFilter,
      renameOpen, renameValue, ownerOpen, ownerTypeValue, ownerIdValue,
    } = this.state;

    return (
      <div className="manage-referral-templates">
        <div className="mrt-header">
          <h2 className="mrt-header__title">Referral reply templates</h2>
          <p className="mrt-header__subtitle">
            Create and edit the branded letters your team sends in reply to referrals. Use the
            merge fields (like
            {' '}
            <code>_patient_</code>
            ) and Medsender fills them in automatically when a reply is sent.
          </p>
        </div>

        <section className="mrt-section">
          <div className="mrt-section__bar">
            <h3 className="mrt-section__title">Your templates</h3>
            <div className="mrt-toolbar">
              <Button
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={this.openCreateModalFromCta}
              >
                New template
              </Button>
              <Input
                allowClear
                placeholder="Search by name"
                value={search}
                onChange={(e) => this.setState({ search: e.target.value })}
                style={{ width: 220 }}
              />
              <Select
                value={ownerFilter}
                onChange={(value) => this.setState({ ownerFilter: value })}
                style={{ width: 200 }}
                showSearch
                optionFilterProp="children"
              >
                <Option value="all">All owners</Option>
                <Option value="org">Whole organization</Option>
                {departments.map((dept) => (
                  <Option key={dept.id} value={`dept:${dept.id}`}>{dept.name}</Option>
                ))}
              </Select>
              <Select
                value={nameFilter}
                onChange={(value) => this.setState({ nameFilter: value })}
                style={{ width: 240 }}
                showSearch
                optionFilterProp="children"
                dropdownMatchSelectWidth={false}
              >
                <Option value="all">All template names</Option>
                {this.distinctNames().map((n) => (
                  <Option key={n.name} value={n.name}>{`${n.name} (${n.count})`}</Option>
                ))}
              </Select>
              <Select
                value={statusFilter}
                onChange={(value) => {
                  this.setState({ statusFilter: value });
                  trackReferralTemplateEvent({ action: 'status_filter_changed', value });
                }}
                style={{ width: 150 }}
              >
                <Option value="active">Active</Option>
                <Option value="archived">Archived</Option>
                <Option value="all">Active & archived</Option>
              </Select>
            </div>
          </div>
          <Spin spinning={isLoading || busy}>{this.renderList()}</Spin>
        </section>

        {this.renderCreateModal()}

        <Modal
          title="Rename template"
          visible={renameOpen}
          onOk={this.handleConfirmRename}
          onCancel={this.closeRenameModal}
          okText="Rename"
          confirmLoading={busy}
        >
          <div className="mrt-create-field">
            <span className="mrt-create-field__label">Template name</span>
            <Input
              value={renameValue}
              onChange={(e) => this.setState({ renameValue: e.target.value })}
              onPressEnter={this.handleConfirmRename}
            />
          </div>
        </Modal>

        <Modal
          title="Change owner"
          visible={ownerOpen}
          onOk={this.handleConfirmOwner}
          onCancel={this.closeOwnerModal}
          okText="Save"
          confirmLoading={busy}
        >
          <div className="mrt-create-field">
            <span className="mrt-create-field__label">Who can use this template?</span>
            <Select
              value={ownerTypeValue}
              style={{ width: '100%' }}
              onChange={(value) => this.setState({ ownerTypeValue: value, ownerIdValue: null })}
            >
              <Option value="Organization">Whole organization</Option>
              <Option value="Department">A specific department</Option>
            </Select>
          </div>
          {ownerTypeValue === 'Department' && (
            <div className="mrt-create-field">
              <span className="mrt-create-field__label">Department</span>
              <Select
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder="Choose a department"
                value={ownerIdValue || undefined}
                onChange={(value) => this.setState({ ownerIdValue: value })}
              >
                {this.renderDepartmentOptions()}
              </Select>
            </div>
          )}
        </Modal>

        <Drawer
          title={null}
          closable={false}
          placement="right"
          width="92%"
          visible={drawerOpen}
          onClose={this.closeEditor}
          destroyOnClose
          bodyStyle={{ padding: 0, height: '100%' }}
          maskClosable={false}
        >
          {drawerOpen && EditorComponent && docxBuffer && (
            <EditorComponent
              docxBuffer={docxBuffer}
              templateName={editingName}
              saving={isSaving}
              onSave={this.handleSave}
              onCancel={this.closeEditor}
              onDirtyChange={this.handleDirtyChange}
            />
          )}
        </Drawer>
      </div>
    );
  }
}

ManageReferralTemplates.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  starters: PropTypes.arrayOf(PropTypes.object).isRequired,
  departments: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  getReferralTemplates: PropTypes.func.isRequired,
  createReferralTemplate: PropTypes.func.isRequired,
  updateReferralTemplate: PropTypes.func.isRequired,
  deleteReferralTemplate: PropTypes.func.isRequired,
  archiveReferralTemplate: PropTypes.func.isRequired,
  unarchiveReferralTemplate: PropTypes.func.isRequired,
  duplicateReferralTemplate: PropTypes.func.isRequired,
  duplicateStarterTemplate: PropTypes.func.isRequired,
  downloadReferralTemplate: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  templates: state.referralTemplates.templates,
  starters: state.referralTemplates.starters,
  isLoading: state.referralTemplates.isLoading,
  isSaving: state.referralTemplates.isSaving,
  departments: state.referralTemplates.departments,
});

export default connect(mapStateToProps, {
  getReferralTemplates,
  createReferralTemplate,
  updateReferralTemplate,
  deleteReferralTemplate,
  archiveReferralTemplate,
  unarchiveReferralTemplate,
  duplicateReferralTemplate,
  duplicateStarterTemplate,
  downloadReferralTemplate,
})(ManageReferralTemplates);
